import { FC, useCallback, useMemo } from "react";

import { useFullUserProfileContext, UserEntityModalType } from "Bands/contexts";
import { copy } from "shared/assets";
import { Plus, Train } from "shared/assets/icons";
import {
  Button,
  Card,
  HomeBaseTeaser,
  Loader,
  Message,
} from "shared/components";
import { Spacer } from "shared/layout";
import { GridContainer, Icon } from "shared/styles";
import { ITheme } from "shared/types";
import { useFetchingContactDetails } from "Bands/utils";

const baseCopy = copy.userHomeBase;

export const HomeBases: FC = () => {
  const {
    homeBases,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    getItemActions,
  } = useFullUserProfileContext();

  const getActions = useCallback(
    (id: string) =>
      getItemActions({
        handleEdit: () =>
          setShowEditModal({
            id: id,
            type: UserEntityModalType.homeBase,
          }),
        handleDelete: () =>
          setShowDeleteModal({
            id: id,
            type: UserEntityModalType.homeBase,
          }),
      }),
    [getItemActions, setShowDeleteModal, setShowEditModal]
  );

  const { fetchingHomeBases } = useFetchingContactDetails();

  const content = useMemo(() => {
    if (fetchingHomeBases) {
      return <Loader />;
    }

    if (!homeBases?.length) {
      return <Message message={baseCopy.messages.empty} />;
    }

    return (
      <GridContainer>
        {homeBases?.map((base) => (
          <HomeBaseTeaser
            key={base.id}
            {...base}
            actions={getActions(base.id)}
          />
        ))}
      </GridContainer>
    );
  }, [fetchingHomeBases, homeBases, getActions]);

  return (
    <Card icon={Train} title={baseCopy.title}>
      <Spacer size="s">
        {content}
        <Button
          onClick={() => setShowCreateModal(UserEntityModalType.homeBase)}
          data-testid="add_home_base_button"
          theme={ITheme.secondary}
        >
          <Icon as={Plus} />
          <span>{baseCopy.buttons.create}</span>
        </Button>
      </Spacer>
    </Card>
  );
};
